import { Link } from "@tanstack/react-router";
import { ArrowLeftRight, RotateCcw } from "lucide-react";
import { useContext, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { useAuth } from "@/features/auth/use-auth";
import { TenantSwitcherContext } from "@/features/tenant-switcher";
import { VisibilityContext } from "@/features/visibility/visibility-provider";
import { useDeviceBindings, useRestartBinding, useSetBinding } from "./api";
import {
	AGENT_SYNC_ELEMENT_ID,
	bindingErrorMessage,
	fmtDateTime,
	versionWithBuild,
} from "./format";
import { AgentStateChip } from "./status-ui";

/**
 * Dostęp do panelu synchronizacji — ten sam element co sidebar, guard trasy i
 * backend (`sidebar.synchronizacja-apteki`); super_admin zawsze (jak
 * `ensureElementVisible`). Kontekst czytany bez rzucania, bo kafelek bywa
 * renderowany poza `VisibilityProvider` (testy, osadzenia) — wtedy tylko super_admin.
 */
export function useCanSeeAgentSync(): boolean {
	const { hasAnyRole } = useAuth();
	const visibility = useContext(VisibilityContext);
	return hasAnyRole(["super_admin"]) || (visibility?.isVisible(AGENT_SYNC_ELEMENT_ID) ?? false);
}

export interface DeviceSyncSectionProps {
	deviceUuid: string;
	deviceName: string;
	idfirm: number | null;
	tenantId: number | null;
	reachable: boolean;
}

/**
 * Sekcja „Synchronizacja z apteki” w kafelku urządzenia: włącz/wyłącz to
 * stanowisko jako stanowisko sync apteki `idfirm`, stan i wersja usługi,
 * restart, link do karty apteki. Bindingi z JEDNEGO zapytania na całą listę
 * urządzeń (`GET /agent-sync/device-bindings`, wspólny queryKey).
 */
export function DeviceSyncSection({
	deviceUuid,
	deviceName,
	idfirm,
	tenantId,
	reachable,
}: DeviceSyncSectionProps) {
	const canSee = useCanSeeAgentSync();
	const { user } = useAuth();
	// Bez rzucania: kafelek bywa renderowany poza `TenantSwitcherProvider` (testy kafelka).
	const selectedTenantId = useContext(TenantSwitcherContext)?.selectedTenantId ?? null;
	// Panel urządzeń super_admina pokazuje wszystkie organizacje naraz, a API
	// synchronizacji działa w organizacji z przełącznika — binding urządzenia z
	// innej organizacji poszedłby do złego tenanta.
	const effectiveTenant = selectedTenantId ?? user?.tenantId ?? null;
	const otherTenant = tenantId != null && effectiveTenant != null && tenantId !== effectiveTenant;
	const active = canSee && idfirm != null && !otherTenant;
	const bindings = useDeviceBindings(active, selectedTenantId);
	const setBinding = useSetBinding();
	const restart = useRestartBinding();
	const [confirmDisable, setConfirmDisable] = useState(false);

	if (!canSee || idfirm == null) return null;

	const heading = (
		<div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
			<ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
			Synchronizacja z apteki
		</div>
	);

	if (otherTenant) {
		return (
			<div className="space-y-1">
				{heading}
				<p className="text-xs text-muted-foreground">
					Urządzenie z innej organizacji — przełącz organizację, żeby zarządzać synchronizacją.
				</p>
			</div>
		);
	}

	const row = bindings.data?.find((b) => b.idfirm === idfirm);
	const isThis = row?.deviceId === deviceUuid;
	const checked = isThis && row?.enabled === true;
	const otherActive = row?.enabled === true && !isThis;

	const toggle = (enabled: boolean) => {
		setBinding.mutate(
			{ idfirm, deviceId: deviceUuid, enabled },
			{
				onSuccess: () =>
					toast.success(
						enabled
							? "Włączono synchronizację z apteki na tym stanowisku"
							: "Wyłączono synchronizację z apteki",
					),
				onError: (err) =>
					toast.error(
						bindingErrorMessage(
							err,
							(id) => (id === deviceUuid ? deviceName : null),
							otherActive ? row?.deviceId : null,
						),
						{ duration: 8000 },
					),
				onSettled: () => setConfirmDisable(false),
			},
		);
	};

	const loaded = !bindings.isPending && !bindings.error;

	return (
		<div className="space-y-1">
			{/* Stan i wersja usługi w wierszu nagłówka; długi błąd skrócony, pełny w dymku. */}
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
				{heading}
				{loaded && isThis && row ? (
					<>
						<AgentStateChip
							state={row.serviceState ?? (row.enabled ? "starting" : "disabled")}
							title={row.lastSeenAt ? `ostatni kontakt ${fmtDateTime(row.lastSeenAt)}` : undefined}
						/>
						<span
							className="font-mono tabular-nums text-muted-foreground"
							title={
								row.buildInfo
									? `Build usługi sync: ${versionWithBuild(row.serviceVersion, row.buildInfo)}`
									: undefined
							}
						>
							{row.serviceVersion ?? "—"}
						</span>
					</>
				) : null}
				{loaded ? (
					<Link
						to="/synchronizacja/apteki"
						search={{ apteka: idfirm }}
						className="ml-auto text-xs text-primary hover:underline max-md:inline-flex max-md:min-h-9 max-md:items-center"
					>
						Panel apteki
					</Link>
				) : null}
				{/* Zawsze osobna linia; od md skrócony (pełny w dymku), na dotyku bez
				    dymka — zawija się jak `DetailRow` w kafelku. */}
				{loaded && isThis && row?.lastError ? (
					<span
						className="min-w-0 basis-full text-status-error-text max-md:break-words md:truncate"
						title={row.lastError}
					>
						{row.lastError}
					</span>
				) : null}
			</div>
			{bindings.isPending ? (
				<p className="text-xs text-muted-foreground">Wczytywanie stanu synchronizacji…</p>
			) : bindings.error ? (
				<p role="alert" className="text-xs text-destructive">
					{bindings.error.message}
				</p>
			) : (
				<>
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
						<label
							className="flex cursor-pointer items-center gap-2 text-xs text-foreground max-md:min-h-9"
							title="Usługa na tym stanowisku odczytuje bazę ERP apteki i porównuje ją z Source/Target"
						>
							<input
								type="checkbox"
								checked={checked}
								disabled={setBinding.isPending || otherActive}
								onChange={() => (checked ? setConfirmDisable(true) : toggle(true))}
							/>
							Stanowisko synchronizacji tej apteki
						</label>
						{checked ? (
							<Button
								size="sm"
								variant="outline"
								className="ml-auto h-7 gap-1.5 max-md:h-9"
								disabled={!reachable || restart.isPending}
								onClick={() =>
									restart.mutate(idfirm, {
										onSuccess: () => toast.success("Wysłano restart usługi synchronizacji"),
										onError: (err) => toast.error(err.message),
									})
								}
							>
								<RotateCcw className="h-3.5 w-3.5" aria-hidden />
								Restart usługi
							</Button>
						) : null}
					</div>
					{otherActive ? (
						<p className="text-xs text-muted-foreground">
							Włączona na innym stanowisku tej apteki — apteka ma jedno stanowisko sync.
						</p>
					) : null}
				</>
			)}
			<ConfirmDialog
				open={confirmDisable}
				onOpenChange={setConfirmDisable}
				title="Wyłączyć synchronizację z apteki?"
				description={`Usługa na stanowisku „${deviceName}” zostanie zatrzymana, a jej token unieważniony od razu.`}
				variant="warning"
				confirmLabel="Wyłącz"
				isLoading={setBinding.isPending}
				onConfirm={() => toggle(false)}
			/>
		</div>
	);
}
